'use client';

import { useEffect, useMemo, useState } from 'react';

import { shuffle } from 'lodash';

import { MAX_WELCOME_SUGGESTIONS } from '@/common/constants';
import { useConfig } from '@/components/providers/config-provider';
import { readJsonFromSessionStorage, writeJsonToSessionStorage } from '@/lib/storage/safe-storage';
import type { ChatSuggestion } from '@/lib/types/tenant';

/**
 * Returns the subset of suggestions to display, applying session-persistent shuffling when
 * the tenant has `meta.randomizeSuggestions` enabled and the pool exceeds MAX_WELCOME_SUGGESTIONS.
 *
 * @param suggestions - Full list of suggestions for the current locale, or null when not yet loaded.
 * @returns Ordered slice of up to MAX_WELCOME_SUGGESTIONS items. Stable within a session when
 *   randomization is active; truncated from the start otherwise.
 */
export function useRandomizedSuggestions(suggestions: ChatSuggestion[] | null): ChatSuggestion[] {
  const { tenantKey, meta } = useConfig();
  const randomize = meta?.randomizeSuggestions ?? false;
  const locale = meta?.language ?? 'en';

  // SSR and the hydrating first client render must produce the same order;
  // `shuffle` (Math.random) during render would diverge between the two and
  // throw React #418 (text content mismatch). Defer randomization to after
  // mount, where it only ever updates an already-hydrated tree.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];

    const deterministic = suggestions.slice(0, MAX_WELCOME_SUGGESTIONS);
    if (!randomize || suggestions.length <= MAX_WELCOME_SUGGESTIONS || !mounted) {
      return deterministic;
    }

    const storageKey = `bazak:suggestions:${tenantKey}:${locale}`;
    const stored = readJsonFromSessionStorage<ChatSuggestion[]>({ key: storageKey, fallback: [] });
    if (stored.length > 0) return stored;

    const picked = shuffle(suggestions).slice(0, MAX_WELCOME_SUGGESTIONS);
    writeJsonToSessionStorage({ key: storageKey, value: picked });
    return picked;
  }, [suggestions, randomize, tenantKey, locale, mounted]);
}
